import { useEffect, useState } from "react";
import { useLang } from "../OneHealthContext";
import { clearRoster, getSiteId, listSites, setSiteId, syncRoster, waitForRosterIdle } from "../roster";

/* Keyless (no device key) site selection — shown right after Get Started.
   Picking a site scopes read-only roster access (x-site-id) so face auth can
   match against that site's workers. Changing sites wipes the local roster
   cache first (identity boundary — another site's photos must never linger).
   Registration stays disabled in this mode: onboarding needs a device key. */
export default function SiteSelect({ onDone, onBack }) {
  const { tr } = useLang();
  const [sites, setSites] = useState(null); // null = loading, [] = none/error
  const [err, setErr] = useState(false);
  const [sel, setSel] = useState(getSiteId());
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let on = true;
    setSites(null); setErr(false);
    listSites()
      .then((s) => { if (on) setSites(s); })
      .catch(() => { if (on) { setSites([]); setErr(true); } });
    return () => { on = false; };
  }, [reload]);

  // The remembered selection only counts once the fresh list confirms the
  // site still exists — a stale cached id must not silently scope the roster.
  const selValid = !!sel && Array.isArray(sites) && sites.some((s) => String(s.siteId) === String(sel));
  const [wipeErr, setWipeErr] = useState(false);

  const proceed = async () => {
    if (!selValid || busy) return;
    setBusy(true); setWipeErr(false);
    const changed = String(sel) !== String(getSiteId());
    try {
      await waitForRosterIdle();
      if (changed) {
        // Identity boundary: the previous site's cached photos/embeddings must
        // be gone BEFORE the new site is committed. A failed wipe blocks the
        // switch entirely rather than risking cross-site face matching.
        try { await clearRoster(); } catch (e) {
          console.warn("roster wipe failed:", e?.message);
          setWipeErr(true);
          return;
        }
      }
      setSiteId(sel);
      // Best effort: pre-sync the site roster so face matching has data by the
      // time the camera opens. Failures are non-fatal — FaceScan retries.
      await syncRoster().catch((e) => console.warn("site roster sync failed:", e?.message));
    } finally {
      setBusy(false);
    }
    onDone();
  };

  return (
    <div className="oh-view oh-siteselect">
      <div className="oh-kicker">{tr("identify.siteKicker")}</div>
      <h2 className="oh-h2">{tr("identify.siteTitle")}</h2>
      <p className="oh-dim oh-site-sub">{tr("identify.siteSub")}</p>

      {sites === null ? (
        <div className="oh-site-loading"><div className="oh-spin" /><p>{tr("identify.siteLoading")}</p></div>
      ) : sites.length === 0 ? (
        <div className="oh-site-empty">
          <p>{err ? tr("identify.siteError") : tr("identify.siteNone")}</p>
          <button className="oh-btn ghost sm" onClick={() => setReload((k) => k + 1)}>{tr("common.tryAgain")}</button>
        </div>
      ) : (
        <div className="oh-site-list">
          {sites.map((s) => (
            <button key={s.siteId} type="button"
              className={"oh-site-item" + (String(sel) === String(s.siteId) ? " on" : "")}
              onClick={() => setSel(String(s.siteId))}>
              <span className="oh-site-name">{s.name}</span>
              {String(sel) === String(s.siteId) && <span className="oh-site-check">✓</span>}
            </button>
          ))}
        </div>
      )}

      {wipeErr && <p className="oh-site-err">{tr("identify.siteError")}</p>}
      <div className="oh-site-actions">
        <button className="oh-btn ghost sm" onClick={onBack}>‹ {tr("common.back")}</button>
        <button className="oh-btn" disabled={!selValid || busy} onClick={proceed}>
          {busy ? tr("identify.sitePreparing") : tr("identify.siteContinue")}
        </button>
      </div>
    </div>
  );
}
